import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PaymentOptions {
  plan: "basic" | "premium";
  onSuccess: () => void;
  onError?: (error: string) => void;
}

interface ReviseOptions {
  newPlan: "basic" | "premium";
  onSuccess: () => void;
  onError?: (error: string) => void;
}

interface CancelOptions {
  onSuccess: () => void;
  onError?: (error: string) => void;
}

export function useRazorpayPayment() {
  const [loadingPlan, setLoadingPlan] = useState<"basic" | "premium" | null>(null);
  const [isRevising, setIsRevising] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiatePayment = async ({ plan, onSuccess, onError }: PaymentOptions) => {
    setLoadingPlan(plan);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load payment gateway");
      }

      // Get session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Please login to continue");
      }

      // Create subscription
      const { data: subscriptionData, error: subscriptionError } = await supabase.functions.invoke(
        "create-razorpay-subscription",
        {
          body: { plan },
        }
      );

      if (subscriptionError || !subscriptionData) {
        console.error("Subscription creation error:", subscriptionError);
        throw new Error(subscriptionError?.message || "Failed to create subscription");
      }

      // Get user profile for prefill
      const { data: profile } = await supabase
        .from("profiles")
        .select("email, full_name")
        .eq("id", session.user.id)
        .maybeSingle();

      // Open Razorpay checkout for subscription
      const options = {
        key: subscriptionData.key_id,
        subscription_id: subscriptionData.subscription_id,
        name: "StoryPlay",
        description: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan - Monthly Subscription`,
        handler: async function (response: any) {
          try {
            // Verify subscription authentication
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
              "verify-razorpay-payment",
              {
                body: {
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  plan: plan,
                },
              }
            );

            if (verifyError || !verifyData?.success) {
              throw new Error(verifyError?.message || "Payment verification failed");
            }

            toast({
              title: "Subscription Activated!",
              description: `You are now subscribed to the ${plan} plan. Billing will renew automatically each month.`,
            });

            onSuccess();
          } catch (err: any) {
            console.error("Payment verification error:", err);
            toast({
              title: "Payment Verification Failed",
              description: err.message || "Please contact support if amount was deducted.",
              variant: "destructive",
            });
            onError?.(err.message);
          } finally {
            setLoadingPlan(null);
          }
        },
        prefill: {
          email: profile?.email || session.user.email,
          name: profile?.full_name || "",
        },
        theme: {
          color: "#6366f1",
        },
        modal: {
          ondismiss: function () {
            setLoadingPlan(null);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      console.error("Payment initiation error:", error);
      toast({
        title: "Payment Error",
        description: error.message || "Failed to initiate payment",
        variant: "destructive",
      });
      onError?.(error.message);
      setLoadingPlan(null);
    }
  };

  const reviseSubscription = async ({ newPlan, onSuccess, onError }: ReviseOptions) => {
    setIsRevising(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Please login to continue");
      }

      const { data, error } = await supabase.functions.invoke(
        "revise-razorpay-subscription",
        {
          body: { new_plan: newPlan },
        }
      );

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Failed to change plan");
      }

      if (data.immediate) {
        toast({
          title: "Plan Upgraded!",
          description: data.message,
        });
      } else {
        toast({
          title: "Plan Change Scheduled",
          description: data.message,
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error("Plan revision error:", error);
      toast({
        title: "Plan Change Failed",
        description: error.message || "Failed to change plan",
        variant: "destructive",
      });
      onError?.(error.message);
    } finally {
      setIsRevising(false);
    }
  };

  const cancelSubscription = async ({ onSuccess, onError }: CancelOptions) => {
    setIsCancelling(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Please login to continue");
      }

      const { data, error } = await supabase.functions.invoke(
        "cancel-razorpay-subscription",
        {
          body: {},
        }
      );

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Failed to cancel subscription");
      }

      toast({
        title: "Subscription Cancelled",
        description: data.message,
      });

      onSuccess();
    } catch (error: any) {
      console.error("Cancellation error:", error);
      toast({
        title: "Cancellation Failed",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
      onError?.(error.message);
    } finally {
      setIsCancelling(false);
    }
  };

  return {
    initiatePayment,
    reviseSubscription,
    cancelSubscription,
    loadingPlan,
    isLoading: loadingPlan !== null,
    isRevising,
    isCancelling,
  };
}
